import type { CSSProperties } from "react";
import "./MultilingualBento.css";

const languagePills = [
  { countryCode: "us", label: "English" },
  { countryCode: "es", label: "Espanol" },
  { countryCode: "fr", label: "Francais" },
  { countryCode: "de", label: "Deutsch" },
  { countryCode: "ps", label: "Arabic" },
  { countryCode: "in", label: "Hindi" },
  { countryCode: "jp", label: "Japanese" },
  { countryCode: "br", label: "Portugues" },
  { countryCode: "kr", label: "Korean" },
  { countryCode: "nl", label: "Dutch" },
  { countryCode: "cn", label: "Chinese" },
  { countryCode: "it", label: "Italian" },
];

/** Native user queries divided into 3 streams to ensure unique countries per row */
const ribbonStream1 = [
  { countryCode: "ps", text: "هل أحصل على شهادة بعد إنهاء دورة \"تعلم الفوتوشوب\"؟" },
  { countryCode: "es", text: "¿Puedo cambiar mi plan?" },
  { countryCode: "de", text: "Wo ist meine Bestellung?" },
  { countryCode: "jp", text: "返品はできますか？" },
];

const ribbonStream2 = [
  { countryCode: "fr", text: "Annuler mon abonnement ?" },
  { countryCode: "us", text: "Do you ship internationally?" },
  { countryCode: "kr", text: "환불 절차가 어떻게 되나요?" },
  { countryCode: "it", text: "Posso avere un rimborso?" },
];

const ribbonStream3 = [
  { countryCode: "br", text: "Como cancelar minha assinatura?" },
  { countryCode: "in", text: "क्या मुझे रिफंड मिल सकता है?" },
  { countryCode: "nl", text: "Hoe wijzig ik mijn abonnement?" },
  { countryCode: "cn", text: "我怎么取消订阅？" },
];

function RibbonBubbles({ items, durationSec }: { items: { countryCode: string; text: string }[], durationSec: number }) {
  // Repeat items to ensure the strip is long enough to fill wide screens seamlessly
  const stripItems = [...items, ...items, ...items, ...items];

  return (
    <div
      className="ml-ribbon-row ml-ribbon-row--flow-right"
      style={{ "--ml-ribbon-duration": `${durationSec}s` } as CSSProperties}
    >
      <div className="ml-ribbon-strip" aria-hidden="true">
        {stripItems.map((msg, i) => (
          <div key={`a-${i}`} className="ml-chatbot-message ml-chatbot-message--user">
            <img
              className="ml-bubble-flag"
              src={`https://flagcdn.com/w40/${msg.countryCode}.png`}
              alt=""
              width={18}
              height={13}
              loading="eager"
              decoding="async"
            />
            <p className="ml-bubble-text">{msg.text}</p>
          </div>
        ))}
      </div>
      <div className="ml-ribbon-strip" aria-hidden="true">
        {stripItems.map((msg, i) => (
          <div key={`b-${i}`} className="ml-chatbot-message ml-chatbot-message--user">
            <img
              className="ml-bubble-flag"
              src={`https://flagcdn.com/w40/${msg.countryCode}.png`}
              alt=""
              width={18}
              height={13}
              loading="eager"
              decoding="async"
            />
            <p className="ml-bubble-text">{msg.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MultilingualBento() {
  return (
    <section className="ml-section" id="languages" aria-labelledby="ml-heading">
      <div className="ml-shell">
        <header className="ml-head">
          <span className="ml-eyebrow">Multilingual by Design</span>
          <h2 id="ml-heading" className="ml-heading">
            Well Spoken in every Language
          </h2>
        </header>

        <div className="ml-grid" aria-label="Multilingual capability bento grid">
          <article className="ml-card ml-card-impact">
            <div className="ml-card-top">
              <span className="ml-tag">Global Coverage</span>
            </div>
            <div className="ml-impact-number">140+</div>
            <p className="ml-impact-copy">languages and dialects, one consistent brand voice.</p>
            <div className="ml-pill-cloud" role="list" aria-label="Supported languages">
              {languagePills.map((item) => (
                <span className="ml-pill" key={item.countryCode + item.label} role="listitem">
                  <img
                    className="ml-pill-flag"
                    src={`https://flagcdn.com/w40/${item.countryCode}.png`}
                    alt={`${item.label} flag`}
                    loading="lazy"
                    width="20"
                    height="15"
                  />
                  <span>{item.label}</span>
                </span>
              ))}
            </div>
          </article>

          <article className="ml-card ml-card-chat">
            <div className="ml-card-top">
              <span className="ml-tag">Live stream</span>
              <span className="ml-stat">∞ languages</span>
            </div>
            <p className="ml-ribbon-caption">Same agent. Every language. Non-stop.</p>
            <div className="ml-chat-shell" aria-hidden="true">
              <div className="ml-ribbon-viewport">
                <div className="ml-ribbon-fade ml-ribbon-fade--left" />
                <div className="ml-ribbon-fade ml-ribbon-fade--right" />
                <div className="ml-ribbon-stage">
                  <RibbonBubbles items={ribbonStream1} durationSec={80} />
                  <RibbonBubbles items={ribbonStream2} durationSec={110} />
                  <RibbonBubbles items={ribbonStream3} durationSec={95} />
                </div>
              </div>
            </div>
          </article>

          <article className="ml-card ml-card-speed">
            <div className="ml-card-top">
              <span className="ml-tag">Response Behavior</span>
            </div>
            <p className="ml-speed-copy">Language in. Same language out.</p>
            <div className="ml-speed-number">&lt;1s</div>
            <p className="ml-speed-sub">match and respond instantly</p>
          </article>

          <article className="ml-card ml-card-text-block">
            <h3>Consistent Quality in Languages</h3>
            <p>
              Deliver accurate, natural, and reliable translations powered by AI, so your brand voice remains consistent, professional, and trustworthy worldwide.
            </p>
          </article>

          <article className="ml-card ml-card-text-block ml-card-localization">
            <svg className="ml-card-bg-icon" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">
              <path d="M62.4,101c-1.5-2.1-2.1-3.4-1.8-3.9c0.2-0.5,1.6-0.7,3.9-0.5c2.3,0.2,4.2,0.5,5.8,0.9c1.5,0.4,2.8,1,3.8,1.7  c1,0.7,1.8,1.5,2.3,2.6c0.6,1,1,2.3,1.4,3.7c0.7,2.8,0.5,4.7-0.5,5.7c-1.1,1-2.6,0.8-4.6-0.6c-2.1-1.4-3.9-2.8-5.5-4.2  C65.5,105.1,63.9,103.2,62.4,101z M40.7,190.1c4.8-2.1,9-4.2,12.6-6.4c3.5-2.1,6.6-4.4,9.3-6.8c2.6-2.3,5-4.9,7-7.7  c2-2.7,3.8-5.8,5.4-9.2c1.3,1.2,2.5,2.4,3.8,3.5c1.2,1.1,2.5,2.2,3.8,3.4c1.3,1.2,2.8,2.4,4.3,3.8c1.5,1.4,3.3,2.8,5.3,4.5  c0.7,0.5,1.4,0.9,2.1,1c0.7,0.1,1.7,0,3.1-0.6c1.3-0.5,3-1.4,5.1-2.8c2.1-1.3,4.7-3.1,7.9-5.4c1.6-1.1,2.4-2,2.3-2.7  c-0.1-0.7-1-1-2.7-0.9c-3.1,0.1-5.9,0.1-8.3-0.1c-2.5-0.2-5-0.6-7.4-1.4c-2.4-0.8-4.9-1.9-7.5-3.4c-2.6-1.5-5.6-3.6-9.1-6.2  c1-3.9,1.8-8,2.4-12.4c0.3-2.5,0.6-4.3,0.8-5.6c0.2-1.2,0.5-2.4,0.9-3.3c0.3-0.8,0.4-1.4,0.5-1.9c0.1-0.5-0.1-1-0.4-1.6  c-0.4-0.5-1-1.1-1.9-1.7c-0.9-0.6-2.2-1.4-3.9-2.3c2.4-0.9,5.1-1.7,7.9-2.6c2.7-0.9,5.7-1.8,8.8-2.7c3-0.9,4.5-1.9,4.6-3.1  c0.1-1.2-0.9-2.3-3.2-3.5c-1.5-0.8-2.9-1.1-4.3-0.9c-1.4,0.2-3.2,0.9-5.4,2.2c-0.6,0.4-1.8,0.9-3.4,1.6c-1.7,0.7-3.6,1.5-6,2.5  c-2.4,1-5,2-7.8,3.1c-2.9,1.1-5.8,2.2-8.7,3.2c-2.9,1.1-5.7,2-8.2,2.8c-2.6,0.8-4.6,1.4-6.1,1.6c-3.8,0.8-5.8,1.6-5.9,2.4  c0,0.8,1.5,1.6,4.4,2.4c1.2,0.3,2.3,0.6,3.1,0.6c0.8,0.1,1.7,0.1,2.5,0c0.8-0.1,1.6-0.3,2.4-0.5c0.8-0.3,1.7-0.7,2.8-1.1  c1.6-0.8,3.9-1.7,6.9-2.8c2.9-1,6.6-2.4,11.2-4c0.9,2.7,1.4,6,1.4,9.8c0,3.8-0.4,8.1-1.4,13c-1.3-1.1-2.7-2.3-4.2-3.6  c-1.5-1.3-2.9-2.6-4.3-3.9c-1.6-1.5-3.2-2.5-4.7-3c-1.6-0.5-3.4-0.5-5.5,0c-3.3,0.9-5,1.9-4.9,3.1c0,1.2,1.3,1.8,3.8,1.9  c0.9,0.1,1.8,0.3,2.7,0.6c0.9,0.3,1.9,0.9,3.2,1.8c1.3,0.9,2.9,2.2,4.7,3.8c1.8,1.6,4.2,3.7,7,6.3c-1.2,2.9-2.6,5.6-4.1,8  c-1.5,2.5-3.4,5-5.5,7.3c-2.2,2.4-4.7,4.8-7.7,7.2c-3,2.5-6.6,5.1-10.8,7.8c-4.3,2.8-6.5,4.7-6.5,5.6C35,192.1,37,191.7,40.7,190.1z   M250.5,81.8v165.3l-111.6-36.4L10.5,253.4V76.1l29.9-10V10.4l81.2,28.7L231.3,2.6v73.1L250.5,81.8z M124.2,50.6L22.3,84.6v152.2  l101.9-33.9V50.6L124.2,50.6z M219.4,71.9V19L138.1,46L219.4,71.9z M227,201.9L196.5,92L176,85.6l-30.9,90.8l18.9,5.9l5.8-18.7  l31.9,10l5.7,22.3L227,201.9z M174.8,147.7l22.2,6.9l-10.9-42.9L174.8,147.7z" />
            </svg>
            <div className="ml-card-text-content">
              <h3>Instant Localization</h3>
              <p>
                Adapt your content for local audiences in seconds, ensuring cultural relevance, accessibility, and stronger engagement across different markets.
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
